import { A } from '@adornis/base/env-info.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { Course } from '@chancenwerk/shared/courses/Course.js';
import type { ChromiumButton } from '@chancenwerk/client/design/components/cr-button.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import {
  ExternerMitarbeiter,
  findSimilarGuestMitarbeiter,
} from '@chancenwerk/shared/master-data-management/users/entities/ExternerMitarbeiter.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { switchMap } from 'rxjs';

@customElement('board-dialog-add-guest-schuko-course')
export class BoardDialogAddGuestSchuKoCourse extends ChromiumDialog<string> {
  @property({ attribute: false }) kpfID: string = '';
  @property({ attribute: false }) courses: Course[] = [];

  @state() _schuKoToCreate = new RXController<ExternerMitarbeiter>(
    this,
    new ExternerMitarbeiter({
      guestAccount: true,
      vorname: '',
      nachname: '',
      email: '',
      handynummer: '',
      username: `dummy-${Math.floor(Math.random() * 1000000)}@chancenwerk.de`,
    }),
  );

  _finishing = false;
  @state() _form = new FormController(this, this._schuKoToCreate.observable);
  @state() _similar = new RXController(
    this,
    this._form.document?.whenChanging.pipe(
      switchMap(async () => {
        if (
          !this._form.document?.vorname ||
          this._form.document.vorname.length === 0 ||
          !this._form.document.nachname ||
          this._form.document.nachname.length === 0
        ) {
          return [] as ExternerMitarbeiter[];
        }
        const res = await findSimilarGuestMitarbeiter(this._form.document)({
          _id: 1,
          ...ChancenwerkUser.toStringSelectionSet,
          handynummer: 1,
          email: 1,
        });
        return res;
      }),
    ),
  );

  _submitButtonRef: Ref<ChromiumButton> = createRef();

  static override get element_name() {
    return 'board-dialog-add-guest-schuko-course';
  }

  override heading = 'Gast-SchuKo hinzufügen';

  override content() {
    return html`<cr-flex
      space="md"
      ${css({
        width: this._showSimilar() ? '800px' : '400px',
      })}
    >
      <cr-grid columns="${this._showSimilar() ? '1fr 1fr' : '1fr'}" space="md">
        ${this._inputContent()} ${this._similarContent()}
      </cr-grid>
      <cr-button ${css({ maxWidth: '200px' })} @click=${async () => this._save()} ${ref(this._submitButtonRef)}>
        Erstellen
      </cr-button>
    </cr-flex>`;
  }

  private _showSimilar() {
    if ((this._similar.value?.length ?? 0) === 0 || this._finishing) {
      return false;
    }
    return true;
  }

  private _inputContent() {
    if (this._finishing) {
      return html`<cr-text>Gast-Nutzer wird erstellt...</cr-text>`;
    }

    return html` <cr-flex space="md">
      <cr-input placeholder="Vorname" ${this._form.field('vorname')}> </cr-input>
      <cr-input placeholder="Nachname" ${this._form.field('nachname')}> </cr-input>
      <cr-flex space="md" padding="sm" ${css({ border: `3px solid ${this.colors.primary}`, borderRadius: '15px' })}>
        <cr-input placeholder="Handynummer" ${this._form.field('handynummer')}> </cr-input>
        <cr-flex crossaxis-center><cr-text>Oder</cr-text></cr-flex>
        <cr-input placeholder="E-Mail" ${this._form.field('email')}> </cr-input>
      </cr-flex>
    </cr-flex>`;
  }

  private _similarContent() {
    if (!this._showSimilar()) {
      return nothing;
    }

    return html`<cr-flex space="md">
      <cr-text bold>Ähnliche Gast-LeKo gefunden:</cr-text>
      ${repeat(
        this._similar.value ?? [],
        item => item._id,
        item => {
          return html`<cr-flex
            space="sm"
            horizontal
            space-between
            crossaxis-center
            ${css({ borderBottom: '1px solid rgba(0,0,0,0.1)', paddingBottom: this.spacing.sm })}
          >
            <cr-flex space="sm">
              <cr-text bold>${item.vorname} ${item.nachname}</cr-text>
              <cr-text>${item.email} ${item.handynummer}</cr-text>
            </cr-flex>
            <cr-button
              @click=${async () => {
                await this._save(item._id);
                await this.close(item._id);
              }}
            >
              Hinzufügen
            </cr-button>
          </cr-flex>`;
        },
      )}
    </cr-flex>`;
  }

  private async _save(id?: string, force = false) {
    if (this.courses.length === 0) {
      void ChromiumSnackbar.show('Kein Kurs gewählt!');
      return;
    }

    if (this._submitButtonRef.value) {
      this._submitButtonRef.value.loading = true;
    }

    if (!force && (this._similar.value?.length ?? 0) > 0) {
      if (!(await XDialog.confirm('Ähnliche Gast-SchuKo gefunden, sicher dass Sie einen neuen erstellen wollen?'))) {
        if (this._submitButtonRef.value) {
          this._submitButtonRef.value.loading = false;
        }
        return;
      }
    }

    try {
      this._finishing = true;

      if (
        !force &&
        (this._schuKoToCreate.value.vorname?.length === 0 ||
          this._schuKoToCreate.value.nachname?.length === 0 ||
          (this._schuKoToCreate.value.email?.length === 0 && this._schuKoToCreate.value.handynummer?.length === 0))
      ) {
        ChromiumSnackbar.show('Eingabe nicht vollständig!');
        return;
      }

      if (this._schuKoToCreate.value.email === '') {
        this._schuKoToCreate.value.email = `dummy-${A.getGloballyUniqueID()}@chancenwerk.de`;
      }

      id ??= await this._schuKoToCreate.value.create();
      const schuko = await getByID(
        ExternerMitarbeiter,
        id,
      )({
        ...ChancenwerkUser.toStringSelectionSet,
        username: 1,
      });
      if (!schuko) {
        throw new Error('SchuKo wurde nicht erstellt');
      }

      this._schuKoToCreate.next(schuko);
      for (const c of this.courses) {
        Course.assignUserToCourse(this._schuKoToCreate.value, c, {
          arrayToAssign: 'SchuKo',
        });
        await c.save();
      }

      await this.close(id);
    } catch (err: any) {
      void ChromiumSnackbar.show(err.message as string);
    } finally {
      if (this._submitButtonRef.value) {
        this._submitButtonRef.value.loading = false;
      }
    }

    this._finishing = false;
  }
}
